import { useState, useEffect, useContext } from "react";
import api from "../../utils/api";
import "./styles.scss";
import { Context } from "../../context/Contex";

export default function Section() {
    const { loggedInUser, permissions, hasPermission, permissionsLoading } = useContext(Context);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [allUsers, setAllUsers] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState([]); // Permissões que o usuário pode atribuir

    // Estados para mudança de senha
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    // Estados para criar novo usuário
    const [newUserData, setNewUserData] = useState({
        nomeCompleto: '',
        apelido: '',
        cpf: '',
        email: '',
        pass: '',
        permission: ''
    });
    const [createUserMessage, setCreateUserMessage] = useState({ type: '', text: '' });

    // Carregar permissões disponíveis quando permissions estiver carregado
    useEffect(() => {
        if (permissions && !permissionsLoading) {
            fetchAllPermissions();
        }
    }, [permissions, permissionsLoading]);

    useEffect(() => {
        if (hasPermission('userManagement') && activeTab === 'manage') {
            fetchAllUsers();
        }
    }, [activeTab, hasPermission]);

    const fetchAllPermissions = async () => {
        try {
            const response = await api.get('/permissions', {
                validateStatus: (status) => status < 500 // Aceita 403 como resposta válida
            });

            if (response.data.success) {
                const allPerms = response.data.data;
                
                setAllPermissions(allPerms);

                // 🔒 Filtrar apenas permissões de nível igual ou inferior
                const userLevel = permissions?.level ?? 999;
                const available = allPerms.filter(perm => perm.level >= userLevel);

                setAvailablePermissions(available);

                // Define a primeira permissão disponível como padrão
                if (available.length > 0) {
                    setNewUserData(prev => ({
                        ...prev,
                        permission: available[0]._id
                    }));
                }
            } else {
                // Trata resposta com success: false (sem permissão)
                // Aqui NÃO vai logar erro no console
                return;
            }
        } catch (error) {
            // Outros erros reais (rede, 500, etc)
            console.error('Erro ao buscar permissões:', error);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await api.get('/users/all');
            if (response.data.success) {
                setAllUsers(response.data.users);
            }
        } catch (error) {
            console.error('Erro ao buscar usuários:', error);
            setCreateUserMessage({
                type: 'error',
                text: error.response?.data?.message || 'Erro ao buscar usuários'
            });
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordMessage({ type: '', text: '' });

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'As senhas não coincidem!' });
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setPasswordMessage({ type: 'error', text: 'A nova senha deve ter no mínimo 8 caracteres!' });
            return;
        }

        try {
            const response = await api.put('/users/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });

            if (response.data.success) {
                setPasswordMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }
        } catch (error) {
            setPasswordMessage({
                type: 'error',
                text: error.response?.data?.message || 'Erro ao alterar senha'
            });
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreateUserMessage({ type: '', text: '' });

        try {
            const response = await api.post('/users/create', newUserData);

            if (response.data.success) {
                setCreateUserMessage({ type: 'success', text: 'Usuário criado com sucesso!' });
                setNewUserData({
                    nomeCompleto: '',
                    apelido: '',
                    cpf: '',
                    email: '',
                    pass: '',
                    permission: availablePermissions[0]?._id || ''
                });
                fetchAllUsers();
            }
        } catch (error) {
            setCreateUserMessage({
                type: 'error',
                text: error.response?.data?.message || 'Erro ao criar usuário'
            });
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;

        try {
            const response = await api.delete(`/users/${userId}`);
            if (response.data.success) {
                setCreateUserMessage({ type: 'success', text: 'Usuário excluído com sucesso!' });
                fetchAllUsers();
            }
        } catch (error) {
            setCreateUserMessage({
                type: 'error',
                text: error.response?.data?.message || 'Erro ao excluir usuário'
            });
        }
    };

    const handleUpdateUserPermission = async (userId, newPermissionId) => {
        try {
            const response = await api.put(`/users/${userId}/permission`, {
                permissionId: newPermissionId
            });
            if (response.data.success) {
                setCreateUserMessage({ type: 'success', text: 'Permissão atualizada com sucesso!' });
                fetchAllUsers();
            }
        } catch (error) {
            setCreateUserMessage({
                type: 'error',
                text: error.response?.data?.message || 'Erro ao atualizar permissão'
            });
        }
    };

    // Função auxiliar para obter emoji baseado no level
    const getLevelEmoji = (level) => {
        switch (level) {
            case 0: return '👑';
            case 1: return '👤';
            case 2: return '👁️';
            default: return '🔹';
        }
    };

    // 🔒 Verificar se pode modificar o usuário
    const canModifyUser = (targetUser) => {
        const userLevel = permissions?.level ?? 999;
        return targetUser.permission?.level >= userLevel;
    };

    // 🔒 Obter permissões disponíveis para um usuário específico
    const getAvailablePermissionsForUser = (targetUser) => {
        const userLevel = permissions?.level ?? 999;
        return allPermissions.filter(perm => perm.level >= userLevel);
    };

    if (!loggedInUser) {
        return (
            <div className="profile-error">
                <span>⚠️</span>
                <p>Erro ao carregar dados do usuário</p>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <header className="profile-header">
                <div className="header-content">
                    <div className="user-avatar">
                        <span>{loggedInUser.nomeCompleto.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="user-info">
                        <h1>{loggedInUser.nomeCompleto}</h1>
                        <p className="user-subtitle">@{loggedInUser.apelido}</p>
                        <span
                            className="position-badge"
                            style={{
                                backgroundColor: permissions?.color || '#16a34a',
                                borderColor: permissions?.color || '#16a34a'
                            }}
                        >
                            {getLevelEmoji(permissions?.level)} {permissions?.name || 'Carregando...'}
                        </span>
                    </div>
                </div>
            </header>

            <div className="profile-tabs">
                <button
                    className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    👤 Perfil
                </button>
                <button
                    className={`tab ${activeTab === 'security' ? 'active' : ''}`}
                    onClick={() => setActiveTab('security')}
                >
                    🔐 Segurança
                </button>
                {hasPermission('userManagement') && (
                    <button
                        className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manage')}
                    >
                        👥 Gerenciar Usuários
                    </button>
                )}
            </div>

            <div className="profile-content">
                {/* TAB: PERFIL */}
                {activeTab === 'profile' && (
                    <div className="profile-section">
                        <h2>📋 Informações Pessoais</h2>
                        <div className="info-grid">
                            <div className="info-card">
                                <label>Nome Completo</label>
                                <p>{loggedInUser.nomeCompleto}</p>
                            </div>
                            <div className="info-card">
                                <label>Apelido</label>
                                <p>@{loggedInUser.apelido}</p>
                            </div>
                            <div className="info-card">
                                <label>CPF</label>
                                <p>{loggedInUser.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}</p>
                            </div>
                            <div className="info-card">
                                <label>Email</label>
                                <p>{loggedInUser.email}</p>
                            </div>
                            <div className="info-card">
                                <label>Nível de Acesso</label>
                                <p>Nível {permissions?.level ?? '-'}</p>
                            </div>
                            {permissions?.description && (
                                <div className="info-card">
                                    <label>Descrição do Cargo</label>
                                    <p>{permissions.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Mostrar permissões ativas */}
                        {permissions?.resources && Object.keys(permissions.resources).length > 0 && (
                            <div className="permissions-card">
                                <h3>🔐 Permissões Ativas</h3>
                                <div className="permissions-list">
                                    {Object.entries(permissions.resources)
                                        .filter(([_, hasAccess]) => hasAccess)
                                        .map(([resource]) => (
                                            <span key={resource} className="permission-badge">
                                                ✓ {resource}
                                            </span>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB: SEGURANÇA */}
                {activeTab === 'security' && (
                    <div className="security-section">
                        <h2>🔐 Alterar Senha</h2>
                        <form onSubmit={handlePasswordChange} className="password-form">
                            <div className="form-group">
                                <label>Senha Atual</label>
                                <input
                                    type="password"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                    placeholder="Digite sua senha atual"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Nova Senha</label>
                                <input
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    placeholder="Mínimo 8 caracteres"
                                    minLength="8"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirmar Nova Senha</label>
                                <input
                                    type="password"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    placeholder="Digite a senha novamente"
                                    required
                                />
                            </div>

                            {passwordMessage.text && (
                                <div className={`message ${passwordMessage.type}`}>
                                    {passwordMessage.type === 'success' ? '✓' : '⚠️'} {passwordMessage.text}
                                </div>
                            )}

                            <button type="submit" className="submit-btn">
                                🔒 Alterar Senha
                            </button>
                        </form>
                    </div>
                )}

                {/* TAB: GERENCIAR USUÁRIOS */}
                {activeTab === 'manage' && hasPermission('userManagement') && (
                    <div className="manage-section">
                        {availablePermissions.length > 0 ? (
                            <>
                                <h2>👥 Criar Novo Usuário</h2>
                                <form onSubmit={handleCreateUser} className="create-user-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Nome Completo</label>
                                            <input
                                                type="text"
                                                value={newUserData.nomeCompleto}
                                                onChange={(e) => setNewUserData({ ...newUserData, nomeCompleto: e.target.value })}
                                                placeholder="Nome completo"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Apelido</label>
                                            <input
                                                type="text"
                                                value={newUserData.apelido}
                                                onChange={(e) => setNewUserData({ ...newUserData, apelido: e.target.value })}
                                                placeholder="@apelido"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>CPF</label>
                                            <input
                                                type="text"
                                                value={newUserData.cpf}
                                                onChange={(e) => setNewUserData({ ...newUserData, cpf: e.target.value.replace(/\D/g, '') })}
                                                placeholder="Apenas números"
                                                maxLength="11"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                value={newUserData.email}
                                                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                                                placeholder="email@exemplo.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Senha</label>
                                            <input
                                                type="password"
                                                value={newUserData.pass}
                                                onChange={(e) => setNewUserData({ ...newUserData, pass: e.target.value })}
                                                placeholder="Mínimo 8 caracteres"
                                                minLength="8"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Permissão</label>
                                            <select
                                                value={newUserData.permission}
                                                onChange={(e) => setNewUserData({ ...newUserData, permission: e.target.value })}
                                                required
                                            >
                                                {availablePermissions.map(perm => (
                                                    <option key={perm._id} value={perm._id}>
                                                        {getLevelEmoji(perm.level)} {perm.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {createUserMessage.text && (
                                        <div className={`message ${createUserMessage.type}`}>
                                            {createUserMessage.type === 'success' ? '✓' : '⚠️'} {createUserMessage.text}
                                        </div>
                                    )}

                                    <button type="submit" className="submit-btn">
                                        ➕ Criar Usuário
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="warning-box">
                                <span>⚠️</span>
                                <p>Você não tem permissões disponíveis para criar novos usuários.</p>
                            </div>
                        )}

                        <div className="users-list">
                            <h2>📋 Usuários Cadastrados</h2>
                            <div className="users-table">
                                <div className="table-header">
                                    <div>Usuário</div>
                                    <div>Email</div>
                                    <div>Permissão</div>
                                    <div>Ações</div>
                                </div>
                                {allUsers.map(u => {
                                    const canModify = canModifyUser(u);
                                    const userAvailablePerms = getAvailablePermissionsForUser(u);

                                    return (
                                        <div key={u._id} className="table-row">
                                            <div className="user-cell">
                                                <div className="user-avatar-small">
                                                    {u.nomeCompleto.charAt(0).toUpperCase() || ""}
                                                </div>
                                                <div>
                                                    <strong>{u.nomeCompleto}</strong>
                                                    <span className="user-apelido">@{u.apelido}</span>
                                                </div>
                                            </div>
                                            <div>{u.email}</div>
                                            <div>
                                                {canModify && u._id !== loggedInUser._id ? (
                                                    <select
                                                        value={u.permission?._id || ''}
                                                        onChange={(e) => handleUpdateUserPermission(u._id, e.target.value)}
                                                        className="position-select"
                                                    >
                                                        {userAvailablePerms.map(perm => (
                                                            <option key={perm._id} value={perm._id}>
                                                                {getLevelEmoji(perm.level)} {perm.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span
                                                        className="position-badge"
                                                        style={{
                                                            backgroundColor: u.permission?.color || '#16a34a',
                                                            opacity: canModify ? 1 : 0.6
                                                        }}
                                                        title={!canModify ? "Você não pode modificar este usuário" : ""}
                                                    >
                                                        {getLevelEmoji(u.permission?.level)} {u.permission?.name}
                                                        {!canModify && ' 🔒'}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                {u._id !== loggedInUser._id && canModify && (
                                                    <button
                                                        onClick={() => handleDeleteUser(u._id)}
                                                        className="delete-btn"
                                                        title="Excluir usuário"
                                                    >
                                                        🗑️
                                                    </button>
                                                )}
                                                {u._id !== loggedInUser._id && !canModify && (
                                                    <span
                                                        className="locked-icon"
                                                        title="Usuário protegido - nível superior ao seu"
                                                    >
                                                        🔒
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}